<template>
  <div class="product-info-availability">
    <div class="head">
      <h1 class="title">
        Доступно в {{ filteredByMetro.length || '' }} {{ filteredByMetro.length | decl('магазин', ['е', 'ах', 'ах']) }} города
        <span class="product-info-availability__change-city" @click="changeCity">
          {{ CITY.name }}
        </span>
      </h1>

      <ui-segmented
        v-model="view"
        class="tabs"
        :labels="Object.values(viewTypes)"
      />

      <availability-filter
        ref="availability-filter"
        :metro-specs="metroSpecs"
        :is-in-store="isInStore"
        @change="metro = $event"
        @in-store-change="isInStore = $event"
        @update="onUpdateFilters"
      />
    </div>

    <div
      v-if="ACTIVE_SKU.id"
      class="stores"
    >
      <div v-if="view === viewTypes.list">
        <div
          v-if="filteredByMetro.length"
          class="availability-list"
        >
          <vue-client-only>
            <div
              v-if="!adaptive.isMobile"
              class="legend availability-grid"
            >
              <span class="availability-grid__address">Адрес</span>
              <span class="availability-grid__delay">Наличие</span>
              <span class="availability-grid__can-pickup ">Можно забрать</span>
              <span class="availability-grid__end" />
            </div>
          </vue-client-only>

          <availability-list-item
            v-for="store in filteredByMetro"
            :key="store.id"
            :info="store"
            @add-to-cart="onAddToCartInList"
          />
        </div>
        <page-info
          v-else
          class="page-info"
          title="По вашему запросу нет доступных магазинов"
        />
      </div>

      <stores-map
        v-else
        popup-by-click
        cart-button-is-active
        :active-store-id="activeStoreId"
        class="availability-map"
        :stores="filteredByMetro"
        @change="onChangeActiveStoreId"
      />
    </div>
    <page-info
      v-else
      class="page-info"
      title="Для поиска товара в магазинах розничной сети необходимо выбрать размер"
    />
  </div>
</template>

<script>
/* Vuex */
import { mapActions, mapGetters } from 'vuex';
import VueClientOnly from 'vue-client-only';

/* Components */
import PageInfo from '@/components/common/page-info';
import AvailabilityFilter from '@/components/product/availability-filter';
import StoresMap from '@/components/stores/stores-map';
import AvailabilityListItem from '@/components/product/availability-list-item';
import { calcDistanceByCoords } from '@/utils/distance-utils';
import { numToFixed } from '@/utils/number-utils';
import { productGtmEvents } from '@/gtm/product';
import { getGeoCoords } from '@/utils/geo-utils';

export const VIEW_TYPES = {
  list: 'Список',
  map: 'Карта',
};

export default {
  name: 'product-info-availability',

  components: {
    AvailabilityListItem,
    AvailabilityFilter,
    StoresMap,
    PageInfo,
    VueClientOnly,
  },

  inject: ['adaptive'],

  provide: {
    gtmEvents: {
      addToCartOnMap: (...params) => productGtmEvents.availability.addToCartOnMap(...params),
    },
  },

  data() {
    return {
      view: VIEW_TYPES.list,
      activeStoreId: '',
      metro: {
        name: '',
        value: '',
      },
      isInStore: false,
      userGeoCoords: null,
    };
  },

  computed: {
    ...mapGetters({
      AVAILABILITY_LIST: 'product/availability',
      ACTIVE_SKU: 'product/activeSku',
      STORES: 'stores/stores',
      CITY: 'user/city',
      PRODUCT: 'product/item',
    }),

    viewTypes: () => VIEW_TYPES,

    stores() {
      return this.AVAILABILITY_LIST.length
          ? this.AVAILABILITY_LIST.map(p => {
            return {
              ...p.store,
              nearestDate: p.nearestDate,
              availableSkus: p.availableSkus,
              skuIds: p.skuIds,
              delayed: p.delayed
            }
          })
          : [];
    },

    filteredBySize() {
      return this.stores.filter(store => { // фильтрация магазинов по размеру
        return (store.availableSkus.some(sku => sku.id === this.ACTIVE_SKU.id))
        && store.status === 'open' // выводим только открытые
      }).sort((a, b) => {
        // если есть гео информация пользователя и магазинов, то сортируем по расстоянию
        if (this.userGeoCoords && a?.geo && b?.geo) {
          const distanceA = this.getStoreDistance(a); // расстояние до магазина A
          const distanceB = this.getStoreDistance(b); // расстояние до магазина B

          // если расстояние разное, сортируем по возрастанию расстояния до магазина
          // если расстояние одинаковое, сортируем по дате
          if (distanceA !== distanceB) return distanceA - distanceB;
        }

        // по умолчанию сортируем по дате
        return this.getDeliveryDate(a) - this.getDeliveryDate(b);
      });
    },

    filteredByDelay() {
      return this.filteredBySize.filter(store => {
        if (this.isInStore) {
          return store.availableSkus.some(sku => sku.id === this.ACTIVE_SKU.id
              && sku.delivery?.delayed === false);
        }

        return true;
      });
    },

    // Выводится в список выдачи
    filteredByMetro() { // необходимая прослойка фильтрации, чтобы изменение фильтра метро не изменяло спеки
      return this.filteredByDelay.filter(store => {
        return this.metro.name && this.metro.name !== 'Не выбрана' ? store.metro.some(({name})=> name.includes(this.metro.name)) : true})
    },

    metroSpecs() {
      return [...this.filteredBySize.reduce((acc, { metro = [] } = {}) => {
        metro.forEach((metro = {}) => {
          if (!acc.some(m => m.name === metro.name && m.line?.color === metro.line?.color))
            acc.push({name: metro.name, value: metro.name})
        });
        return acc;
      }, [])]
      .sort((a, b) => { // сортируем в алфавитном порядке
        if (a.name < b.name) { return -1; }
        if (a.name > b.name) { return 1; }
        return 0;
      });
    }
  },

  watch: {
    'ACTIVE_SKU.id'() { // сброс карточки на карте, при изменении размера
      if (this.activeStoreId) this.activeStoreId = '';
    },

    view(value, oldValue) {
      if (value === oldValue) return;
      if (value === VIEW_TYPES.list) {
        productGtmEvents.availability.clickListView({ cityName: this.CITY?.name });
      }
      if (value === VIEW_TYPES.map) {
        productGtmEvents.availability.clickMapView({ cityName: this.CITY?.name });
      }
    },
  },

  created() {
    if (this.$isServer) return;
    this.LOAD_SHOPS();
  },

  async mounted() {
    try {
      // при показе компонента, пытаемся получить геопозицию пользователя
      this.userGeoCoords = await getGeoCoords();
    } catch (e) {
      this.userGeoCoords = null;
    }
  },

  methods: {
    ...mapActions({
      LOAD_SHOPS: 'product/loadAvailabilityList',
      LOAD_PRODUCT: 'product/loadProduct',
      UPDATE_CITY: 'user/updateCity'
    }),

    async changeCity() {
      try {
        const city = await this.$city.changeCity();
        this.$city.requestConfirmation({ city }).then(async () => {
          await this.LOAD_SHOPS(); // подгружаем города
          await this.LOAD_PRODUCT({ routePush: false }); // обновляем инфу
          this.onShowProductDelivery();
          this.onUpdateFilters();
          this.$city.hideCityConfirmation();
        })
      } catch (error) {
        console.log(error);
      }
    },

    getStoreDistance(store) {
      return numToFixed(calcDistanceByCoords(
        this.userGeoCoords?.latitude,
        this.userGeoCoords?.longitude,
        store.geo.latitude,
        store.geo.longitude,
      ), 3); // округляем до 3 знаков, т.к. показывает лучший результат сортировки
    },

    getDeliveryDate(store) {
      // если параметр delayed = false - это "сегодня" и nearestDate не получаем.
      // для корректной сортировки вписываем "сегодняшнюю" дату вручную
      let delivery = store.availableSkus.find(sku => sku.id === this.ACTIVE_SKU.id)?.delivery;
      return new Date(!delivery.delayed ? new Date().toISOString() : delivery.nearestDate);
    },

    onChangeActiveStoreId(storeId) {
      this.activeStoreId = storeId;
      if (!storeId) return;
      productGtmEvents.availability.chooseStoreOnMap({
        productCode: this.PRODUCT?.code,
        cityName: this.CITY?.name,
        storeId,
      });
    },

    onUpdateFilters(key) {
      // при любых изменениях на карте, отправляем событие в GTM
      if (this.view === VIEW_TYPES.map) {
        productGtmEvents.availability.changeMap({
          productCode: this.PRODUCT?.code,
          cityName: this.CITY?.name,
        });
      }

      // при изменении цвета или размера, отправляем событие о показе блока доставки
      if (['size', 'color'].includes(key)) {
        this.onShowProductDelivery();
      }
    },

    onAddToCartInList(storeId) {
      productGtmEvents.availability.addToCartInList({
        productCode: this.PRODUCT?.code,
        cityName: this.CITY?.name,
        storeId,
      });
    },

    onShowProductDelivery() {
      productGtmEvents.card.showDelivery({
        productCode: this.PRODUCT?.code,
        cityName: this.CITY?.name,
        activeSku: this.ACTIVE_SKU,
      });
    },
  }
};
</script>

<style lang="scss">
@import "../availability-grid.scss";

.product-info-availability {
  margin-top: 24px;

  & > .head {
    & > .title {
      font-size: 20px;
      line-height: 28px;
      font-weight: normal;

      span {
        color: $blue;
        cursor: pointer;
      }
    }

    & > .tabs {
      margin-top: 12px;
    }
  }

  & > .stores {
    margin-top: 16px;

    & > .availability-map {
      height: 480px;
    }
  }
}

@include breakpoint("tablet") {
  .product-info-availability {
    & > .head {
      display: flex;
      align-items: center;
      flex-wrap: wrap;

      & > .title {
        font-size: 24px;
        line-height: 28px;
        margin-top: 0;
      }

      & > .tabs {
        margin-top: 0;
        margin-left: auto;
      }
    }

    & > .stores {
      margin-top: 32px;

      .availability-list {
        & > .legend {
          padding: 16px 12px;
          font-size: 12px;
          line-height: 16px;
          color: $gray;
          border-bottom: 1px solid $gray_lu;
        }
      }

      & > .availability-map {
        height: 580px;
      }
    }
  }
}

@include breakpoint("desktop") {
  .product-info-availability {
    & > .head {
      & > .title {
        font-size: 32px;
        line-height: 48px;
      }
    }

    & > .stores {
      .availability-list {

        & > .legend {
          padding: 16px 20px;
          font-size: 14px;
        }
      }

      & > .availability-map {
        height: 580px;
      }
    }
  }
}
</style>
